import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiSearch, FiPlus, FiEdit2, FiTrash2, FiPhone, FiMessageCircle, FiMail, FiX, FiList, FiGrid, FiFile, FiFileText } from 'react-icons/fi';
import PinModal from '../components/PinModal';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Suppliers = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [pinAction, setPinAction] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin;
  const canDelete = isAdmin;
  const canAdd = isAdmin;

  useEffect(() => { if (isAdmin) fetchSuppliers(); }, [search]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get(`/suppliers?search=${search}`);
      setSuppliers(response.data);
    } catch (error) { toast.error('Erreur lors du chargement des fournisseurs'); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPinAction('save');
    setShowPinModal(true);
  };

  const executeSave = async () => {
    try {
      if (selectedSupplier) {
        await api.put(`/suppliers/${selectedSupplier.id}`, { ...formData, pin: '020260' });
        toast.success('Fournisseur modifié avec succès');
      } else {
        await api.post('/suppliers', { ...formData, pin: '020260' });
        toast.success('Fournisseur ajouté avec succès');
      }
      setShowModal(false);
      setFormData({ name: '', email: '', phone: '', contact_person: '', address: '' });
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) { toast.error(error.response?.data?.message || 'Erreur'); }
  };

  const handleDelete = (supplier) => {
    setSelectedSupplier(supplier);
    setPinAction('delete');
    setShowPinModal(true);
  };

  const executeDelete = async () => {
    try {
      await api.delete(`/suppliers/${selectedSupplier.id}`, { data: { pin: '020260' } });
      toast.success('Fournisseur supprimé avec succès');
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error) { toast.error('Erreur lors de la suppression'); }
  };

  const handlePinConfirm = async () => {
    if (pinAction === 'save') await executeSave();
    else if (pinAction === 'delete') await executeDelete();
    setShowPinModal(false);
    setPinAction(null);
  };

  const [formData, setFormData] = useState({ name: '', email: '', phone: '', contact_person: '', address: '' });
  const openEditModal = (supplier) => {
    setSelectedSupplier(supplier);
    setFormData(supplier);
    setShowModal(true);
  };

  const exportToExcel = () => {
    const exportData = suppliers.map(s => ({ 'Nom': s.name, 'Téléphone': s.phone, 'Email': s.email || '', 'Contact': s.contact_person || '', 'Adresse': s.address || '' }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.writeFile(XLSX.utils.book_new(), ws, `fournisseurs_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(18);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Liste des fournisseurs', pageWidth / 2, 32, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.text(`Total fournisseurs: ${suppliers.length}`, 14, 55);
    const tableHeaders = [['Nom', 'Téléphone', 'Email', 'Contact']];
    const tableRows = suppliers.slice(0, 50).map(s => [s.name, s.phone, s.email || '-', s.contact_person || '-']);
    doc.autoTable({ startY: 65, head: tableHeaders, body: tableRows, theme: 'striped', styles: { fontSize: 8 }, headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] } });
    doc.save(`fournisseurs_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  if (!isAdmin) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Fournisseurs</h1>
        <div className="flex gap-3">
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFile size={18} /> Excel</button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiFileText size={18} /> PDF</button>
          <div className="flex gap-2 border-l pl-3">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiList size={18} /></button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-green-100 text-green-600' : 'bg-gray-100'}`}><FiGrid size={18} /></button>
          </div>
          {canAdd && (<button onClick={() => { setSelectedSupplier(null); setFormData({ name: '', email: '', phone: '', contact_person: '', address: '' }); setShowModal(true); }} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"><FiPlus size={18} /> Nouveau fournisseur</button>)}
        </div>
      </div>

      <div className="card mb-6"><div className="relative"><FiSearch className="absolute left-3 top-1/2 text-gray-400" /><input type="text" placeholder="Rechercher..." className="input-field pl-10" value={search} onChange={(e) => setSearch(e.target.value)} /></div></div>

      {viewMode === 'list' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b"><tr><th className="pb-3">Nom</th><th className="pb-3">Téléphone</th><th className="pb-3">Email</th><th className="pb-3">Contact</th><th className="pb-3 text-center">Actions</th></tr></thead>
            <tbody>{suppliers.map((supplier) => (<tr key={supplier.id} className="border-b hover:bg-gray-50"><td className="py-3 font-medium">{supplier.name}</td><td>{supplier.phone}</td><td>{supplier.email || '-'}</td><td>{supplier.contact_person || '-'}</td><td className="text-center"><div className="flex justify-center gap-2">{canEdit && <button onClick={() => openEditModal(supplier)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiEdit2 size={16} /></button>}<button onClick={() => window.location.href = `tel:${supplier.phone}`} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><FiPhone size={16} /></button><button onClick={() => { const phone = supplier.phone?.replace(/[^0-9]/g, ''); if (phone) window.open(`https://wa.me/${phone}`, '_blank'); }} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiMessageCircle size={16} /></button>{supplier.email && <button onClick={() => window.location.href = `mailto:${supplier.email}`} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg"><FiMail size={16} /></button>}{canDelete && <button onClick={() => handleDelete(supplier)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}</div></td></tr>))}</tbody>
          </table>
        </div>
      )}

      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {suppliers.map((supplier) => (<div key={supplier.id} className="card"><div className="flex justify-between"><div><h3 className="font-semibold">{supplier.name}</h3><p className="text-sm text-gray-500">{supplier.phone}</p></div><div className="flex gap-2">{canEdit && <button onClick={() => openEditModal(supplier)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><FiEdit2 size={16} /></button>}{canDelete && <button onClick={() => handleDelete(supplier)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg"><FiTrash2 size={16} /></button>}</div></div><div className="border-t pt-3 mt-2"><div className="flex gap-2"><button onClick={() => window.location.href = `tel:${supplier.phone}`} className="flex-1 bg-gray-100 py-1 rounded text-sm">Appeler</button><button onClick={() => window.open(`https://wa.me/${supplier.phone?.replace(/[^0-9]/g, '')}`, '_blank')} className="flex-1 bg-gray-100 py-1 rounded text-sm">WhatsApp</button></div></div></div>))}
        </div>
      )}

      {showModal && (<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"><div className="bg-white rounded-xl p-6 w-full max-w-md"><div className="flex justify-between mb-4"><h2 className="text-xl font-semibold">{selectedSupplier ? 'Modifier' : 'Nouveau fournisseur'}</h2><button onClick={() => setShowModal(false)}><FiX size={20} /></button></div><form onSubmit={handleSubmit}><input type="text" placeholder="Nom *" className="input-field mb-4" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /><input type="tel" placeholder="Téléphone *" className="input-field mb-4" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required /><input type="email" placeholder="Email" className="input-field mb-4" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /><input type="text" placeholder="Personne de contact" className="input-field mb-4" value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} /><textarea placeholder="Adresse" className="input-field mb-6" rows="2" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /><div className="flex gap-3"><button type="button" onClick={() => setShowModal(false)} className="flex-1 btn-secondary">Annuler</button><button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg">{selectedSupplier ? 'Modifier' : 'Ajouter'}</button></div></form></div></div>)}

      <PinModal isOpen={showPinModal} onClose={() => { setShowPinModal(false); setPinAction(null); setSelectedSupplier(null); }} onConfirm={handlePinConfirm} title="Code PIN requis" message="Action nécessite une autorisation." />
    </div>
  );
};

export default Suppliers;